import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  alpha,
  useTheme,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

type QuestionType = 'text' | 'multiple_choice' | 'yes_no' | 'yes_no_na' | 'number' | 'date' | 'signature' | 'text_header' | 'email_check' | 'image_type';

interface Question {
  question_id?: string;
  question_text: string;
  question_type: QuestionType;
  is_required: boolean;
  options?: string[];
  show_conditions?: any;
}

interface Condition {
  questionId: string;
  operator: string;
  value: string;
}

interface ConditionGroup {
  logic?: 'AND' | 'OR';
  conditions: (Condition | ConditionGroup)[];
}

interface ShowConditions {
  mode: 'simple' | 'advanced';
  conditions?: Condition[];
  logic?: 'AND' | 'OR';
  conditionGroups?: ConditionGroup;
}

interface ConditionalLogicBuilderProps {
  currentQuestionIndex: number;
  questions: Question[];
  showConditions?: ShowConditions | null;
  onChange: (conditions: ShowConditions | null) => void;
}

const ConditionalLogicBuilder: React.FC<ConditionalLogicBuilderProps> = ({
  currentQuestionIndex,
  questions,
  showConditions,
  onChange,
}) => {
  const theme = useTheme();
  const [advancedMode, setAdvancedMode] = useState(showConditions?.mode === 'advanced' || false);

  // Get only previous questions (questions that appear before this one)
  const previousQuestions = questions.slice(0, currentQuestionIndex);

  // Simple mode: single condition
  const [simpleCondition, setSimpleCondition] = useState<Condition>(
    showConditions?.mode === 'simple' && showConditions.conditions && showConditions.conditions.length > 0
      ? showConditions.conditions[0]
      : { questionId: '', operator: 'equals', value: '' }
  );

  // Advanced mode: multiple conditions with AND/OR
  const [advancedConditions, setAdvancedConditions] = useState<Condition[]>(
    showConditions?.mode === 'advanced' && showConditions.conditions
      ? showConditions.conditions as Condition[]
      : []
  );
  const [advancedLogic, setAdvancedLogic] = useState<'AND' | 'OR'>(
    showConditions?.mode === 'advanced' && showConditions.logic ? showConditions.logic : 'AND'
  );

  const [isEnabled, setIsEnabled] = useState(!!showConditions);
  const [circularDependencyWarning, setCircularDependencyWarning] = useState<string | null>(null);

  // Sync state when showConditions prop changes (important for editing templates)
  useEffect(() => {
    if (showConditions) {
      setIsEnabled(true);
      setAdvancedMode(showConditions.mode === 'advanced');
      
      if (showConditions.mode === 'simple' && showConditions.conditions && showConditions.conditions.length > 0) {
        setSimpleCondition(showConditions.conditions[0]);
      }
      
      if (showConditions.mode === 'advanced' && showConditions.conditions) {
        setAdvancedConditions(showConditions.conditions as Condition[]);
        setAdvancedLogic(showConditions.logic || 'AND');
      }
      
      console.log('🔄 ConditionalLogicBuilder: Synced with showConditions prop:', showConditions);
    } else {
      setIsEnabled(false);
      setSimpleCondition({ questionId: '', operator: 'equals', value: '' });
      setAdvancedConditions([]);
    }
  }, [showConditions]);

  // Debug logging (after state declarations)
  console.log('🔍 ConditionalLogicBuilder Debug:', {
    currentQuestionIndex,
    totalQuestions: questions.length,
    previousQuestionsCount: previousQuestions.length,
    previousQuestions: previousQuestions.map((q, idx) => ({ 
      id: q.question_id, 
      index: idx,
      text: q.question_text.substring(0, 30),
      hasId: !!q.question_id
    })),
    showConditions,
    isEnabled
  });

  // Helper function to get unique identifier for a question
  // Uses question_id if available, otherwise uses index as fallback
  const getQuestionIdentifier = (question: Question, index: number): string => {
    return question.question_id || `temp-${index}`;
  };

  // Helper function to find a question by identifier (handles both real IDs and temp IDs)
  const findQuestionByIdentifier = (identifier: string): Question | undefined => {
    if (identifier.startsWith('temp-')) {
      const index = parseInt(identifier.replace('temp-', ''), 10);
      return previousQuestions[index];
    }
    return previousQuestions.find((q) => q.question_id === identifier);
  };

  // Check for circular dependencies
  const checkCircularDependency = (targetQuestionId: string): boolean => {
    if (!targetQuestionId) return false;

    const visited = new Set<string>();
    const currentQuestionId = questions[currentQuestionIndex]?.question_id;

    const hasCircularDep = (questionId: string): boolean => {
      if (!questionId) return false;
      if (questionId === currentQuestionId) return true; // Found a loop!
      if (visited.has(questionId)) return false; // Already checked this path

      visited.add(questionId);

      // Find this question and check its dependencies
      const question = questions.find((q) => q.question_id === questionId);
      if (!question || !question.show_conditions) return false;

      // Get all question IDs this question depends on
      const conditions = question.show_conditions;
      let dependentQuestionIds: string[] = [];

      if (conditions.mode === 'simple' && conditions.conditions) {
        dependentQuestionIds = conditions.conditions.map((c: Condition) => c.questionId);
      } else if (conditions.mode === 'advanced' && conditions.conditions) {
        dependentQuestionIds = (conditions.conditions as Condition[]).map((c) => c.questionId);
      }

      // Recursively check each dependency
      for (const depId of dependentQuestionIds) {
        if (hasCircularDep(depId)) return true;
      }

      return false;
    };

    return hasCircularDep(targetQuestionId);
  };

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does not contain' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'answered', label: 'Is answered' },
    { value: 'notAnswered', label: 'Is not answered' },
  ];

  const getAvailableValuesForQuestion = (questionId: string): string[] => {
    const question = findQuestionByIdentifier(questionId);
    if (!question) return [];

    switch (question.question_type) {
      case 'yes_no':
        return ['Yes', 'No'];
      case 'yes_no_na':
        return ['Yes', 'No', 'N/A'];
      case 'multiple_choice':
        return question.options || [];
      default:
        return [];
    }
  };

  const needsTextInput = (questionId: string): boolean => {
    const question = findQuestionByIdentifier(questionId);
    if (!question) return false;

    return ['text', 'number', 'date'].includes(question.question_type);
  };

  const getInputTypeForQuestion = (questionId: string): string => {
    const question = findQuestionByIdentifier(questionId);
    if (!question) return 'text';

    switch (question.question_type) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  };

  const getOperatorsForQuestion = (questionId: string) => {
    const question = findQuestionByIdentifier(questionId);
    if (!question) return operators;

    switch (question.question_type) {
      case 'yes_no':
      case 'yes_no_na':
      case 'multiple_choice':
        return operators.filter((op) =>
          ['equals', 'notEquals', 'answered', 'notAnswered'].includes(op.value)
        );
      case 'text':
        return operators;
      case 'number':
        return operators.filter((op) => !['contains', 'notContains'].includes(op.value));
      case 'date':
        return operators.filter((op) => 
          ['equals', 'notEquals', 'greaterThan', 'lessThan', 'answered', 'notAnswered'].includes(op.value)
        );
      case 'signature':
        return operators.filter((op) => ['answered', 'notAnswered'].includes(op.value));
      default:
        return operators.filter((op) => ['answered', 'notAnswered'].includes(op.value));
    }
  };

  const handleEnableToggle = (enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      onChange(null);
    } else {
      // Enable with current values
      handleSimpleConditionChange();
    }
  };

  const handleSimpleConditionChange = (updatedCondition?: Condition) => {
    const conditionToUse = updatedCondition || simpleCondition;
    
    console.log('📤 handleSimpleConditionChange called:', {
      isEnabled,
      conditionToUse,
      willCallOnChange: isEnabled && !!conditionToUse.questionId
    });
    
    if (!isEnabled || !conditionToUse.questionId) {
      onChange(null);
      return;
    }

    const conditionsObject: ShowConditions = {
      mode: 'simple' as const,
      conditions: [conditionToUse],
    };
    
    console.log('📤 Calling onChange with:', conditionsObject);
    onChange(conditionsObject);
  };

  const handleAdvancedConditionsChange = (newConditions: Condition[], newLogic: 'AND' | 'OR') => {
    if (!isEnabled || newConditions.length === 0) {
      onChange(null);
      return;
    }

    const conditionsObject: ShowConditions = {
      mode: 'advanced' as const,
      logic: newLogic,
      conditions: newConditions,
    };

    onChange(conditionsObject);
  };

  const addAdvancedCondition = () => {
    const newCondition: Condition = { questionId: '', operator: 'equals', value: '' };
    const newConditions = [...advancedConditions, newCondition];
    setAdvancedConditions(newConditions);
    handleAdvancedConditionsChange(newConditions, advancedLogic);
  };

  const removeAdvancedCondition = (index: number) => {
    const newConditions = advancedConditions.filter((_, i) => i !== index);
    setAdvancedConditions(newConditions);
    handleAdvancedConditionsChange(newConditions, advancedLogic);
  };

  const updateAdvancedCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...advancedConditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setAdvancedConditions(newConditions);
    handleAdvancedConditionsChange(newConditions, advancedLogic);
  };

  const toggleAdvancedMode = () => {
    const newMode = !advancedMode;
    setAdvancedMode(newMode);

    if (newMode) {
      // Switching to advanced mode - convert simple to advanced
      if (simpleCondition.questionId) {
        setAdvancedConditions([simpleCondition]);
        handleAdvancedConditionsChange([simpleCondition], 'AND');
      }
    } else {
      // Switching to simple mode - use first condition
      if (advancedConditions.length > 0) {
        setSimpleCondition(advancedConditions[0]);
        const conditionsObject: ShowConditions = {
          mode: 'simple' as const,
          conditions: [advancedConditions[0]],
        };
        onChange(conditionsObject);
      }
    }
  };

  const getConditionPreview = (): string => {
    if (!isEnabled || !showConditions) return 'Always show';

    if (showConditions.mode === 'simple' && showConditions.conditions && showConditions.conditions.length > 0) {
      const cond = showConditions.conditions[0];
      const question = findQuestionByIdentifier(cond.questionId);
      const operator = operators.find((op) => op.value === cond.operator);
      
      if (!question) return 'Invalid condition';

      const questionText = question.question_text.substring(0, 30) + (question.question_text.length > 30 ? '...' : '');
      
      if (cond.operator === 'answered') {
        return `Show if "${questionText}" is answered`;
      } else if (cond.operator === 'notAnswered') {
        return `Show if "${questionText}" is not answered`;
      }
      
      return `Show if "${questionText}" ${operator?.label.toLowerCase()} "${cond.value}"`;
    }

    if (showConditions.mode === 'advanced') {
      const condCount = showConditions.conditions?.length || 0;
      const logic = showConditions.logic || 'AND';
      return `Show if ${condCount} condition${condCount !== 1 ? 's' : ''} match (${logic})`;
    }

    return 'Always show';
  };

  if (previousQuestions.length === 0) {
    return (
      <Alert severity="info" icon={<InfoIcon />}>
        Conditional logic is not available for the first question. Add more questions to enable this feature.
      </Alert>
    );
  }

  return (
    <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.info.main, 0.02) }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              🔀 Conditional Logic
            </Typography>
            <FormControlLabel
              control={<Switch checked={isEnabled} onChange={(e) => handleEnableToggle(e.target.checked)} size="small" />}
              label="Enable"
            />
          </Box>

          {isEnabled && (
            <>
              {circularDependencyWarning && (
                <Alert severity="error" onClose={() => setCircularDependencyWarning(null)}>
                  {circularDependencyWarning}
                </Alert>
              )}

              <Alert severity="info" icon={<InfoIcon />} sx={{ fontSize: '0.875rem' }}>
                <strong>Preview:</strong> {getConditionPreview()}
              </Alert>

              {!advancedMode ? (
                // Simple Mode
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Show this question only if:
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Question</InputLabel>
                      <Select
                        value={simpleCondition.questionId}
                        label="Question"
                        onChange={(e) => {
                          const selectedQuestionId = e.target.value;
                          console.log('🔄 Question selected:', selectedQuestionId);
                          
                          // Check for circular dependency
                          if (checkCircularDependency(selectedQuestionId)) {
                            console.warn('⚠️ Circular dependency detected');
                            setCircularDependencyWarning(
                              'This would create a circular dependency! Please choose a different question.'
                            );
                            return;
                          }
                          
                          setCircularDependencyWarning(null);
                          const newCondition = { ...simpleCondition, questionId: selectedQuestionId, value: '' };
                          console.log('✅ Setting new condition:', newCondition);
                          setSimpleCondition(newCondition);
                          handleSimpleConditionChange(newCondition);
                        }}
                      >
                        {previousQuestions.map((q, idx) => {
                          const identifier = getQuestionIdentifier(q, idx);
                          return (
                            <MenuItem key={identifier} value={identifier}>
                              {q.question_text.substring(0, 50)}
                              {q.question_text.length > 50 ? '...' : ''}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={simpleCondition.operator}
                        label="Operator"
                        onChange={(e) => {
                          const newCondition = { ...simpleCondition, operator: e.target.value };
                          setSimpleCondition(newCondition);
                          handleSimpleConditionChange(newCondition);
                        }}
                        disabled={!simpleCondition.questionId}
                      >
                        {getOperatorsForQuestion(simpleCondition.questionId).map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {!['answered', 'notAnswered'].includes(simpleCondition.operator) && (
                      needsTextInput(simpleCondition.questionId) ? (
                        <TextField
                          size="small"
                          label="Value"
                          type={getInputTypeForQuestion(simpleCondition.questionId)}
                          value={simpleCondition.value}
                          onChange={(e) => {
                            const newCondition = { ...simpleCondition, value: e.target.value };
                            setSimpleCondition(newCondition);
                            handleSimpleConditionChange(newCondition);
                          }}
                          disabled={!simpleCondition.questionId}
                          sx={{ minWidth: 150 }}
                          InputLabelProps={
                            getInputTypeForQuestion(simpleCondition.questionId) === 'date'
                              ? { shrink: true }
                              : undefined
                          }
                        />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={simpleCondition.value}
                            label="Value"
                            onChange={(e) => {
                              const newCondition = { ...simpleCondition, value: e.target.value };
                              setSimpleCondition(newCondition);
                              handleSimpleConditionChange(newCondition);
                            }}
                            disabled={!simpleCondition.questionId}
                          >
                            {getAvailableValuesForQuestion(simpleCondition.questionId).map((val) => (
                              <MenuItem key={val} value={val}>
                                {val}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )
                    )}
                  </Stack>
                </Box>
              ) : (
                // Advanced Mode
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Show this question if:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={advancedLogic}
                        onChange={(e) => {
                          const newLogic = e.target.value as 'AND' | 'OR';
                          setAdvancedLogic(newLogic);
                          handleAdvancedConditionsChange(advancedConditions, newLogic);
                        }}
                      >
                        <MenuItem value="AND">ALL (AND)</MenuItem>
                        <MenuItem value="OR">ANY (OR)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Stack spacing={2}>
                    {advancedConditions.map((condition, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                          {index > 0 && (
                            <Chip label={advancedLogic} size="small" color="primary" sx={{ mb: 1 }} />
                          )}

                          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                            <InputLabel>Question</InputLabel>
                            <Select
                              value={condition.questionId}
                              label="Question"
                              onChange={(e) => {
                                const selectedQuestionId = e.target.value;
                                
                                // Check for circular dependency
                                if (checkCircularDependency(selectedQuestionId)) {
                                  setCircularDependencyWarning(
                                    'This would create a circular dependency! Please choose a different question.'
                                  );
                                  return;
                                }
                                
                                setCircularDependencyWarning(null);
                                updateAdvancedCondition(index, { questionId: selectedQuestionId, value: '' });
                              }}
                            >
                              {previousQuestions.map((q, idx) => {
                                const identifier = getQuestionIdentifier(q, idx);
                                return (
                                  <MenuItem key={identifier} value={identifier}>
                                    {q.question_text.substring(0, 40)}
                                    {q.question_text.length > 40 ? '...' : ''}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={condition.operator}
                              label="Operator"
                              onChange={(e) => updateAdvancedCondition(index, { operator: e.target.value })}
                              disabled={!condition.questionId}
                            >
                              {getOperatorsForQuestion(condition.questionId).map((op) => (
                                <MenuItem key={op.value} value={op.value}>
                                  {op.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {!['answered', 'notAnswered'].includes(condition.operator) && (
                            needsTextInput(condition.questionId) ? (
                              <TextField
                                size="small"
                                label="Value"
                                type={getInputTypeForQuestion(condition.questionId)}
                                value={condition.value}
                                onChange={(e) => updateAdvancedCondition(index, { value: e.target.value })}
                                disabled={!condition.questionId}
                                sx={{ minWidth: 120 }}
                                InputLabelProps={
                                  getInputTypeForQuestion(condition.questionId) === 'date'
                                    ? { shrink: true }
                                    : undefined
                                }
                              />
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Value</InputLabel>
                                <Select
                                  value={condition.value}
                                  label="Value"
                                  onChange={(e) => updateAdvancedCondition(index, { value: e.target.value })}
                                  disabled={!condition.questionId}
                                >
                                  {getAvailableValuesForQuestion(condition.questionId).map((val) => (
                                    <MenuItem key={val} value={val}>
                                      {val}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )
                          )}

                          <Tooltip title="Remove condition">
                            <IconButton size="small" onClick={() => removeAdvancedCondition(index)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    ))}

                    <Button startIcon={<AddIcon />} onClick={addAdvancedCondition} size="small" variant="outlined">
                      Add Condition
                    </Button>
                  </Stack>
                </Box>
              )}

              <Box sx={{ pt: 1, borderTop: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}` }}>
                <Button onClick={toggleAdvancedMode} size="small" variant="text">
                  {advancedMode ? '← Switch to Simple Mode' : 'Switch to Advanced Mode →'}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ConditionalLogicBuilder;

